import React, { useState, useEffect } from "react";
import { auth, db } from "../firebaseConfig";
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from "firebase/firestore";
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Box,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import { useMediaQuery, useTheme } from '@mui/material';
import VisibilityIcon from "@mui/icons-material/Visibility";


const RegistroEmpresa = () => {
  const [empresas, setEmpresas] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentEmpresaId, setCurrentEmpresaId] = useState(null);
  const initialFormData = {
    nombre: "",
    telefono: "",
    email: "",
    rfc: "",
    ciudad: "",
    cp: "",
    colonia: "",
    numeroExt: "",
    numeroInt: "",
    regimenFiscal: "",
    cobrarImpuesto: false,
    tipoMoneda: "",
    metodoPago: [],
    usoCfdi: "",
    archivo: null,
    formaPago: "",
    tiempoPagoCredito: [],
  };
  const [formData, setFormData] = useState(initialFormData);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [isReadOnly, setIsReadOnly] = useState(false);


  useEffect(() => {
    const fetchEmpresas = async () => {
      const empresasSnapshot = await getDocs(collection(db, "empresas"));
      setEmpresas(empresasSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchEmpresas();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (name === "metodoPago") {
      const updatedMetodoPago = checked
        ? [...formData.metodoPago, value]
        : formData.metodoPago.filter((v) => v !== value);
      setFormData({ ...formData, metodoPago: updatedMetodoPago });
    } else if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (type === "file") {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({ ...formData, archivo: reader.result });
      };
      reader.readAsDataURL(files[0]);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = auth.currentUser;
      if (!user) return;
      const userId = user.uid;

      const dataToSave = {
        ...formData,
        userId,
      };

      // Convertir archivo PDF a base64 si existe y es menor a 800 KB
      if (formData.archivo instanceof File) {
        const file = formData.archivo;
        if (file.size > 819200) {
          alert("El archivo es demasiado grande. Usa un PDF de menos de 800 KB.");
          return;
        }

        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        dataToSave.archivo = base64;
      } else if (!formData.archivo) {
        // Eliminar si no se proporciona archivo
        delete dataToSave.archivo;
      }

      if (editMode) {
        const empresaRef = doc(db, "empresas", currentEmpresaId);
        await updateDoc(empresaRef, dataToSave);
        setEmpresas(empresas.map((emp) => emp.id === currentEmpresaId ? { id: currentEmpresaId, ...dataToSave } : emp));
        setAlertMessage("Empresa actualizada exitosamente.");
      } else {
        const newEmpresaRef = await addDoc(collection(db, "empresas"), dataToSave);
        setEmpresas([...empresas, { id: newEmpresaRef.id, ...dataToSave }]);
        setAlertMessage("Empresa agregada exitosamente.");
      }

      // Reset formulario
      setFormVisible(false);
      setEditMode(false);
      setFormData({
        nombre: "",
        telefono: "",
        email: "",
        rfc: "",
        ciudad: "",
        cp: "",
        colonia: "",
        numeroExt: "",
        numeroInt: "",
        regimenFiscal: "",
        cobrarImpuesto: false,
        tipoMoneda: "",
        metodoPago: [],
        usoCfdi: "",
        archivo: null,
        formaPago: "",
        tiempoPagoCredito: [],
      });
      setAlertSeverity("success");
      setAlertOpen(true);
    } catch (error) {
      console.error(error);
      setAlertMessage("Error al guardar la empresa.");
      setAlertSeverity("error");
      setAlertOpen(true);
    }
  };


  const handleDelete = async (empresaId) => {
    try {
      await deleteDoc(doc(db, "empresas", empresaId));
      setEmpresas(empresas.filter((empresa) => empresa.id !== empresaId));
      setAlertMessage("Empresa eliminada exitosamente.");
      setAlertSeverity("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Error al eliminar empresa:", error);
      setAlertMessage("Ocurrió un error al eliminar la empresa.");
      setAlertSeverity("error");
      setAlertOpen(true);
    }
    setOpenDeleteDialog(false);
  };

  const handleEdit = (empresa) => {
    setFormVisible(true);
    setEditMode(true);
    setCurrentEmpresaId(empresa.id);
    setFormData({ ...empresa });
  };


  const handleViewEmpresa = (empresa) => {  
    setCurrentEmpresaId(empresa.id);
    setFormData({ ...empresa });
    setFormVisible(true);
    setIsReadOnly(true);
    setEditMode(true);
    // setOpenDialog(true); // Abrir el modal
  };

  return (
    <Container maxWidth="lg" sx={{ paddingBottom: 2 }}>
      {!formVisible ? (
        <>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>Empresas</Typography>
          <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => {
            setFormData(initialFormData); 
            setEditMode(false); 
            setCurrentEmpresaId(null); 
            setFormVisible(true); 
          }} sx={{ mb: 2 }}>Nueva Empresa</Button>

          {isMobile ? (
            <Box>
              {empresas.map((empresa) => (
                <Card
                  key={empresa.id}
                  sx={{ marginBottom: 2, padding: 2 }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">{empresa.nombre}</Typography>
                    <Typography variant="body2"><strong>Ciudad:</strong> {empresa.ciudad}</Typography>
                    <Typography variant="body2"><strong>Teléfono:</strong> {empresa.telefono}</Typography>
                    <Typography variant="body2"><strong>Email:</strong> {empresa.email}</Typography>
                    <Box mt={1}>
                      {empresa.archivo ? (
                        <IconButton
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = empresa.archivo;
                            link.download = `${empresa.nombre || "empresa"}_${empresa.rfc || "sinRFC"}.pdf`;
                            link.click();
                          }}
                        >
                          <PictureAsPdfIcon color="primary" />
                        </IconButton>
                      ) : (
                        <IconButton disabled>
                          <PictureAsPdfIcon color="disabled" />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 1 }}>
                    <IconButton color="primary" onClick={() => handleViewEmpresa(empresa)}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton color="primary" onClick={() => handleEdit(empresa)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="secondary" onClick={() => handleDelete(empresa.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Ciudad</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Archivo</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {empresas.map((empresa) => (
                    <TableRow key={empresa.id}>
                      <TableCell>{empresa.nombre}</TableCell>
                      <TableCell>{empresa.ciudad}</TableCell>
                      <TableCell>{empresa.telefono}</TableCell>
                      <TableCell>{empresa.email}</TableCell>
                      <TableCell>
                        {empresa.archivo ? (
                          <IconButton
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = empresa.archivo;
                              link.download = `${empresa.nombre || "empresa"}_${empresa.rfc || "sinRFC"}.pdf`;
                              link.click();
                            }}
                          >
                            <PictureAsPdfIcon color="primary" />
                          </IconButton>
                        ) : (
                          <IconButton disabled>
                            <PictureAsPdfIcon color="disabled" />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton color="primary" onClick={() => handleViewEmpresa(empresa)}>
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton color="primary" onClick={() => handleEdit(empresa)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="secondary" onClick={() => handleDelete(empresa.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>

      ) : (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>{editMode ? "Editar Empresa" : "Nueva Empresa"}</Typography>
            {isReadOnly && (
              <IconButton color="secondary" onClick={() => {
                setIsReadOnly(false);
                handleEdit(formData);
              }}>
                <EditIcon />
              </IconButton>
            )}
            <IconButton onClick={() => setFormVisible(false)}><CloseIcon /></IconButton>
          </Box>
          <form onSubmit={handleSubmit}>
            <TextField label="Nombre" name="nombre" value={formData.nombre} onChange={handleChange} fullWidth required margin="normal" disabled={isReadOnly} />
            <TextField label="RFC" name="rfc" value={formData.rfc} onChange={handleChange} fullWidth required margin="normal" sx={{
              '& .MuiInputBase-input': {
                textTransform: 'uppercase',
              },
            }} disabled={isReadOnly} />
            <TextField label="Ciudad" name="ciudad" value={formData.ciudad} onChange={handleChange} fullWidth required margin="normal" disabled={isReadOnly} />
            <TextField label="Código Postal" name="cp" value={formData.cp} onChange={handleChange} fullWidth required margin="normal" disabled={isReadOnly} />
            <TextField label="Colonia" name="colonia" value={formData.colonia} onChange={handleChange} fullWidth required margin="normal" disabled={isReadOnly} />
            <TextField label="Número Exterior" name="numeroExt" value={formData.numeroExt} onChange={handleChange} fullWidth required margin="normal" disabled={isReadOnly} />
            <TextField label="Número Interior" name="numeroInt" value={formData.numeroInt} onChange={handleChange} fullWidth margin="normal" disabled={isReadOnly} />
            <FormControl fullWidth margin="normal" disabled={isReadOnly}>
              <InputLabel>Régimen Fiscal</InputLabel>
              <Select name="regimenFiscal" value={formData.regimenFiscal} onChange={handleChange} required>
                <MenuItem value="Persona Física">Persona Física</MenuItem>
                <MenuItem value="Persona Moral">Persona Moral</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Teléfono" name="telefono" value={formData.telefono} onChange={handleChange} fullWidth required margin="normal" disabled={isReadOnly} />
            <TextField label="Email" name="email" value={formData.email} onChange={handleChange} fullWidth required margin="normal" disabled={isReadOnly} />
            <FormControl fullWidth margin="normal" disabled={isReadOnly}>
              <InputLabel>Tipo de Moneda</InputLabel>
              <Select name="tipoMoneda" value={formData.tipoMoneda} onChange={handleChange} required>
                <MenuItem value="MXN">MXN</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" disabled={isReadOnly}>
              <InputLabel>Forma de Pago</InputLabel>
              <Select name="formaPago" value={formData.formaPago} onChange={handleChange} required>
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Crédito">Crédito</MenuItem>
                <MenuItem value="Transferencia">Transferencia</MenuItem>
                <MenuItem value="Efectivo">Efectivo</MenuItem>
              </Select>
            </FormControl>
            {formData.formaPago === "Crédito" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                {[7, 15, 30].map((dias) => (
                  <FormControlLabel disabled={isReadOnly}
                    key={dias}
                    control={
                      <Checkbox
                        checked={formData.tiempoPagoCredito.includes(String(dias))}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const nuevaLista = checked
                            ? [...formData.tiempoPagoCredito, String(dias)]
                            : formData.tiempoPagoCredito.filter((item) => item !== String(dias));
                          setFormData({ ...formData, tiempoPagoCredito: nuevaLista });
                        }}
                      />
                    }
                    label={`${dias} días`}
                  />
                ))}
              </Box>
            )}
            {formData.archivo && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                {/* Ícono de descarga (siempre visible) */}
                <a href={formData.archivo} download={`${formData.nombre || "empresa"}_${formData.rfc || "sinRFC"}.pdf`} target="_blank" rel="noopener noreferrer">
                  <IconButton color="primary">
                    <DownloadIcon />
                  </IconButton>
                </a>

                {/* Vista previa solo en escritorio */}
                {!isMobile && (
                  <Box
                    component="iframe"
                    src={formData.archivo}
                    title="Vista previa PDF"
                    sx={{
                      width: '100%',
                      height: 400,
                      border: '1px solid #ccc',
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>
            )}

            {/* Resto del formulario */}
            <Button variant="contained" component="label" sx={{ my: 2 }} disabled={isReadOnly}>
              Subir Archivo PDF
              <input type="file" accept="application/pdf" hidden onChange={handleChange} name="archivo" />
            </Button>
            <Button type="submit" variant="contained" disabled={isReadOnly} fullWidth>{editMode ? "Actualizar Empresa" : "Guardar Empresa"}</Button>
          </form>
        </Box>
      )}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={() => setAlertOpen(false)}>
        <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity}>{alertMessage}</Alert>
      </Snackbar>
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de eliminar esta empresa?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">Cancelar</Button>
          <Button onClick={() => handleDelete(currentEmpresaId)} color="secondary">Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RegistroEmpresa;
